/** Turns REST framework error payloads into sentences a non-technical user can act on. */

export type ApiErrorDetails = {
  messages: string[];
  status?: number;
};

const FIELD_LABELS: Record<string, string> = {
  actions: "Actions",
  attributes: "Attributes",
  auto_eventcode: "Automatic event code",
  description: "Description",
  dev_comment: "Development comment",
  event_code: "Event code",
  event_code_old: "Legacy event code",
  event_contract_name: "Contract name",
  event_descriptions: "Descriptions",
  event_type: "Event type",
  label: "HMI text",
  label_long: "Long HMI text",
  language_code: "Language",
  module: "Module",
  parameters: "Parameters",
  severity: "Severity",
  situation: "Situation",
  version: "Version",
};

/** Keys that carry a plain message rather than naming a form field. */
const UNLABELLED_KEYS = new Set(["detail", "error", "errors", "message", "non_field_errors"]);

const MAX_DEPTH = 4;
const MAX_MESSAGES = 12;
const MAX_MESSAGE_LENGTH = 600;

const labelFor = (key: string): string => {
  if (UNLABELLED_KEYS.has(key)) return "";
  const known = FIELD_LABELS[key];
  if (known) return known;
  const spaced = key.replace(/_/g, " ").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : "";
};

const joinLabel = (parent: string, child: string): string => {
  if (!parent) return child;
  if (!child) return parent;
  return `${parent} → ${child}`;
};

const pushMessage = (label: string, text: string, out: string[]): void => {
  const message = text.trim();
  if (!message || out.length >= MAX_MESSAGES) return;
  const alreadyNamed = label && message.toLowerCase().startsWith(label.toLowerCase());
  out.push(label && !alreadyNamed ? `${label}: ${message}` : message);
};

const collectMessages = (value: unknown, label: string, out: string[], depth = 0): void => {
  if (out.length >= MAX_MESSAGES || value === null || value === undefined) return;

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    pushMessage(label, String(value), out);
    return;
  }

  if (depth >= MAX_DEPTH) return;

  if (Array.isArray(value)) {
    const indexEntries = value.length > 1;
    value.forEach((entry, index) => {
      const entryLabel =
        indexEntries && entry !== null && typeof entry === "object"
          ? joinLabel(label, `#${index + 1}`)
          : label;
      collectMessages(entry, entryLabel, out, depth + 1);
    });
    return;
  }

  if (typeof value === "object") {
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      collectMessages(entry, joinLabel(label, labelFor(key)), out, depth + 1);
    }
  }
};

const stripMarkup = (raw: string): string =>
  raw
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_MESSAGE_LENGTH);

/**
 * `fallback` is used when the response carries no readable content, because the raw
 * `Error.message` of a failed request is only the bare status line.
 */
export const describeApiError = (error: unknown, fallback: string): ApiErrorDetails => {
  const apiError = error as { status?: number; responseBody?: string } | undefined;
  const status = typeof apiError?.status === "number" ? apiError.status : undefined;
  const raw = typeof apiError?.responseBody === "string" ? apiError.responseBody.trim() : "";

  if (raw) {
    try {
      const messages: string[] = [];
      collectMessages(JSON.parse(raw), "", messages);
      if (messages.length > 0) return { messages, status };
    } catch {
      // Gateways and proxies can answer with HTML or plain text instead of JSON.
    }
    const text = stripMarkup(raw);
    if (text) return { messages: [text], status };
  }

  return { messages: [fallback], status };
};
